import React from "react";
import { Col, Row, Typography, Select } from "antd";
import { t } from "i18next";
import CustomModal from "components/CustomModal";
import { Trans } from "react-i18next";
import CustomTable from "components/CustomTable";
import SpaceCustom from "components/_storybooks/SpaceCustom";
import { Link } from "react-router-dom";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import EllipsisText from "components/_storybooks/EllipsisText";
import { useQuery } from "@apollo/client";
import { GET_WAREHOUSES_BY_SELLER } from "graphql/ware-house/query";

const ModalChangeWarehouse = ({
  loading,
  order,
  visibleChangeWarehouse,
  setVisibleChangeWarehouse,
  selectedWarehouseID,
  setSelectedWarehouseID,
  handleConfirmChangeWarehouse,
}) => {
  const sellerID = order?.sellerID || order?.seller?.id;
  const { data } = useQuery(GET_WAREHOUSES_BY_SELLER, {
    variables: { sellerID },
    skip: !sellerID,
    fetchPolicy: "network-only",
  });
  const warehouses = data?.warehouse?.list?.warehouses || [];

  // Kiểm tra từng warehouse có đủ tồn kho không
  const checkWarehouseStock = (warehouseId) => {
    let hasEnoughStock = true;

    order?.items?.forEach((item) => {
      const stock = item?.product?.stocks?.find((s) => s?.warehouse?.id === warehouseId);
      const stockQuantity = stock?.quantity || 0;

      if (stockQuantity < item?.quantity) {
        hasEnoughStock = false;
      }
    });

    return hasEnoughStock;
  };

  // Tạo options với label có màu
  const warehouseOptions = warehouses?.map((item) => {
    const hasEnoughStock = checkWarehouseStock(item.id);
    return (
      <Select.Option key={item.id} value={item.id} label={item.name}>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "8px" }}>
          <span style={{ whiteSpace: "nowrap" }}>{item.name}</span>
          <span
            style={{
              color: hasEnoughStock ? "#00AB78" : "#D2434D",
              fontWeight: "500",
              fontStyle: "italic",
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            {hasEnoughStock ? "Đủ tồn" : "Thiếu"}
          </span>
        </div>
      </Select.Option>
    );
  });

  const renderChildrenInStock = (warehouses, selectedWarehouseID) => {
    return warehouses?.map((warehouse, index) => ({
      title: (
        <span
          style={
            warehouse?.id === selectedWarehouseID ? { textDecoration: "underline #1953D8 2px", color: "#1953D8" } : {}
          }
        >
          <EllipsisText
            style={{ color: warehouse?.id === selectedWarehouseID ? "#1953D8" : "#5b6673" }}
            title={warehouse.name}
            line={2}
          />
        </span>
      ),
      key: index,
      width: warehouse?.name?.length > 30 ? 300 : 120,
      align: "center",
      editable: "false",
      className: warehouse?.id === selectedWarehouseID ? "highlight-col" : "",
      render: (_, record) => {
        const stockQuantity =
          record?.product?.stocks?.filter((stock) => stock?.warehouse?.id === warehouse?.id)[0]?.quantity || 0;
        return (
          <SpaceCustom>
            <Typography.Text>{stockQuantity}</Typography.Text>
            {stockQuantity < record?.quantity && warehouse?.id === selectedWarehouseID && (
              <Typography.Text type="warning">Không đủ tồn kho</Typography.Text>
            )}
          </SpaceCustom>
        );
      },
    }));
  };

  const columns = [
    {
      title: t("order.orderDetail.parameter"),
      width: 250,
      fixed: "left",
      render: (text, record) => {
        return (
          <SpaceCustom>
            <Link target="_blank" to={`/product/detail/${record.productID}`}>
              <Typography.Link strong>{record?.productCode}</Typography.Link>
            </Link>
            <CustomTextHiddenLine text={record?.productName} strong line={2} />
            <Typography style={{ textAlign: "end", fontStyle: "italic" }}>{record?.product?.uom?.name}</Typography>
          </SpaceCustom>
        );
      },
    },
    {
      title: "Số lượng đại lý đặt",
      align: "center",
      width: 100,
      render: (text, record, index) => {
        return (
          <SpaceCustom>
            <Typography.Text>{record?.quantity || "--"}</Typography.Text>
          </SpaceCustom>
        );
      },
    },
    {
      title: "TỒN VAT",
      align: "center",
      width: 100,

      render: (text, record, index) => {
        const quantityRealStockHasVat =
          (record?.product?.stocks && record?.product?.stocks[0].quantityRealStockHasVat) ?? 0;
        return (
          <SpaceCustom>
            <Typography.Text>{quantityRealStockHasVat}</Typography.Text>
            {quantityRealStockHasVat < record?.quantity && record?.vat && (
              <Typography.Text type="warning">Không đủ tồn vat</Typography.Text>
            )}
          </SpaceCustom>
        );
      },
    },
    {
      title: "LOẠI % VAT",
      dataIndex: "vatPercent",
      align: "center",
      width: 120,
      render: (_, record) => {
        return <div>{record?.product?.vatPercent >= 0 ? `${record?.product?.vatPercent}%` : "KCT"}</div>;
      },
      editable: "true",
    },
    {
      title: "Tồn kho khả dụng",
      children: renderChildrenInStock(warehouses, selectedWarehouseID),
      width: 300,
    },
  ];

  return (
    <CustomModal
      title={
        <span style={{ fontSize: "18px", fontWeight: "bold", textAlign: "center" }}>
          <Trans
            i18nKey="v2.order.detail.modalChangeWarehouse.title"
            values={{ orderCode: order?.number || "" }}
            components={{ style: <span style={{ color: "#2246dc", fontWeight: 700 }} /> }}
          ></Trans>
        </span>
      }
      visible={visibleChangeWarehouse}
      onCancel={() => setVisibleChangeWarehouse(false)}
      onOke={handleConfirmChangeWarehouse}
      buttonLoading={loading}
      selfVisibleControlled={false}
      footer={false}
      width="70%"
    >
      <>
        <Row gutter={[16, 16]}>
          <Col>
            <span style={{ fontWeight: 700 }}>{t("v2.order.detail.modalChangeWarehouse.labelSelectWarehouse")}</span>
            <span style={{ color: "#D2434D" }}>*</span>
          </Col>
          <Col span={6}>
            <Select 
              value={selectedWarehouseID} 
              onChange={setSelectedWarehouseID}
              style={{ width: "100%", minWidth: 200 }}
              dropdownMatchSelectWidth={false}
              optionLabelProp="label"
            >
              {warehouseOptions}
            </Select>
          </Col>
          <Col span={24}>
            <span style={{ color: "#ef9351" }}>
              <Trans
                i18nKey="v2.order.detail.modalChangeWarehouse.warning"
                values={{ salesman: order?.salesman?.fullname || "" }}
                components={{ style: <span style={{ color: "#2246dc", fontWeight: 700 }} /> }}
              ></Trans>
            </span>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <CustomTable
              rowKey={(record) => record?.id}
              columns={columns}
              dataSource={order?.items}
              pagination={false}
              scroll={{}}
            />
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default ModalChangeWarehouse;
